import { LoadingButton } from '@mui/lab'
import { Alert, Button, Stack, TextField, Typography } from '@mui/material'
import { useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { usePasswordResetGetQuery } from 'src/api/queries/passwordReset/usePasswordResetGetQuery/usePasswordResetGetQuery'
import BlockedHint from 'src/components/BlockedHint'
import EntryLayout, {
  CONSOLE_STANDFIRST,
} from 'src/components/layout/EntryLayout'
import Page from 'src/components/layout/Page'
import { useBlockedReason } from 'src/components/useBlockedAction'
import SecondFactorInput from 'src/pages/AccountSettingsPage/parts/SecondFactorInput'
import { newPasswordProblem } from 'src/pages/AccountSettingsPage/parts/passwordRules'
import type { SecondFactorValue } from 'src/pages/AccountSettingsPage/parts/secondFactor'
import {
  SECOND_FACTOR_EMPTY,
  secondFactorInput,
} from 'src/pages/AccountSettingsPage/parts/secondFactor'
import { useLogin } from 'src/pages/LoginPage/parts/useLogin'
import LinkPasswordSetup from './LinkPasswordSetup'
import { resetLinkTokenRead } from './resetLinkToken'

// ----------------------------------------------------------------------
// Where a password reset email sends somebody.
//
// The link proves the mailbox, and the page still asks for the code from the
// authenticator app, or a recovery code: a link found in somebody else's
// inbox resets nothing. The authenticator stays as it is. Finishing signs
// out every other session and starts this one.
// ----------------------------------------------------------------------

const Heading = ({ title, body }: { title: string; body: string }) => (
  <Stack spacing={1}>
    <Typography variant="h2">{title}</Typography>
    <Typography variant="body2" color="text.secondary">
      {body}
    </Typography>
  </Stack>
)

const BackToSignIn = () => (
  <Button component={RouterLink} to="/login" variant="text">
    Back to sign in
  </Button>
)

const LinkGone = ({ reason }: { reason?: string }) => (
  <Stack spacing={3}>
    <Heading
      title="This link no longer works"
      body={
        reason ??
        'A reset link works once, for 30 minutes, and asking for another one retires the ones before it.'
      }
    />
    <Button
      component={RouterLink}
      to="/login/forgot"
      variant="contained"
      size="large"
    >
      Ask for a new link
    </Button>
    <BackToSignIn />
  </Stack>
)

const PasswordResetBody = () => {
  const [token] = useState(resetLinkTokenRead)
  const { data, loading, error } = usePasswordResetGetQuery(token)
  const { passwordResetLogin, loading: finishing, error: refusal } = useLogin()
  const [password, setPassword] = useState('')
  const [repeated, setRepeated] = useState('')
  const [answer, setAnswer] = useState<SecondFactorValue>(SECOND_FACTOR_EMPTY)
  const [gone, setGone] = useState(false)

  const problem =
    newPasswordProblem(password, repeated) ??
    (!answer.text.trim() ? 'Enter a code' : null)
  const blocked = useBlockedReason(Boolean(problem))

  if (!token) {
    return (
      <Stack spacing={3}>
        <Heading
          title="This link is incomplete"
          body="Open the reset email again and use its button. Copying the address by hand can leave part of it behind."
        />
        <Button
          component={RouterLink}
          to="/login/forgot"
          variant="outlined"
          size="large"
        >
          Ask for a new link
        </Button>
        <BackToSignIn />
      </Stack>
    )
  }

  if (loading) {
    return <Heading title="Opening your link" body="One moment." />
  }

  if (error || !data) {
    return (
      <Stack spacing={3}>
        <Heading
          title="The link could not be opened"
          body="Check your connection and open the link again."
        />
        <Alert severity="error" role="alert">
          {error?.message ?? 'The link could not be read.'}
        </Alert>
      </Stack>
    )
  }

  if (gone) {
    return <LinkGone reason={refusal} />
  }

  if (data.passwordResetGet.state !== 'valid') {
    return <LinkGone />
  }

  // From Beast staff's reset: the account has no password or app left, so
  // the link sets both, rather than asking for a code from an app it lacks.
  if (data.passwordResetGet.enrol) {
    return (
      <LinkPasswordSetup
        link={{ resetToken: token }}
        email={data.passwordResetGet.email ?? 'your account'}
      />
    )
  }

  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    if (problem) {
      return
    }
    const outcome = await passwordResetLogin(
      token,
      password,
      secondFactorInput(answer),
    )
    if (outcome === 'linkGone') {
      setGone(true)
    }
  }

  return (
    <Stack spacing={3}>
      <Heading
        title="Choose a new password"
        body={`For ${
          data.passwordResetGet.email ?? 'your account'
        }. You will need the code from your authenticator app, or one of your recovery codes.`}
      />

      {refusal && (
        <Alert severity="error" role="alert">
          {refusal}
        </Alert>
      )}

      <Stack component="form" onSubmit={submit} spacing={3}>
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          disabled={finishing}
          fullWidth
        />
        <TextField
          label="New password again"
          type="password"
          autoComplete="new-password"
          value={repeated}
          onChange={(event) => setRepeated(event.target.value)}
          disabled={finishing}
          fullWidth
        />
        <SecondFactorInput
          value={answer}
          onChange={setAnswer}
          disabled={finishing}
        />
        <BlockedHint
          reason={blocked.blocked ? problem : null}
          reasonProps={blocked.reasonProps}
          sx={{ width: '100%' }}
        >
          <LoadingButton
            type="submit"
            variant="contained"
            size="large"
            loading={finishing}
            aria-disabled={blocked.blocked}
            disabled={blocked.blocked}
            fullWidth
          >
            Set the new password
          </LoadingButton>
        </BlockedHint>
        <BackToSignIn />
      </Stack>
    </Stack>
  )
}

const PasswordResetPage = () => (
  <Page title="Reset password">
    <EntryLayout
      headline="Run your fleet."
      standfirst={CONSOLE_STANDFIRST}
      photos
    >
      <PasswordResetBody />
    </EntryLayout>
  </Page>
)

export default PasswordResetPage
